import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Star, Phone, Mail, MapPin, Clock, Euro, Shield } from 'lucide-react';

const TaxiBruxelles = () => {
  const benefits = [
    'Service disponible 24h/24 et 7j/7',
    'Chauffeurs professionnels et expérimentés',
    'Véhicules de luxe récents et entretenus',
    'Prix fixes sans mauvaise surprise',
    'Réservation en ligne rapide et facile',
    'Suivi en temps réel de votre chauffeur',
    'Paiement sécurisé en ligne ou à bord',
    'Service clientèle réactif'
  ];

  const services = [
    {
      title: 'Taxi Aéroport Bruxelles',
      description: 'Transfert vers/depuis les aéroports de Zaventem et Charleroi',
      link: '/taxi-aeroport-bruxelles'
    },
    {
      title: 'Taxi Gare Bruxelles',
      description: 'Prise en charge gares du Midi, Centrale et du Nord',
      link: '/taxi-bruxelles'
    },
    {
      title: 'Chauffeur Privé VIP',
      description: 'Service premium avec véhicules de luxe',
      link: '/private-chauffeur-brussels'
    },
    {
      title: 'Taxi Corporate',
      description: 'Solutions de transport pour entreprises',
      link: '/taxi-bruxelles'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Taxi Bruxelles 24/7 | Service Premium & Prix Fixes | Réservation En Ligne</title>
        <meta name="description" content="Taxi Bruxelles disponible 24h/24. Réservez votre taxi en ligne: prix fixes, chauffeurs pro, véhicules luxe. Service aéroport, gare, quartier européen. Devis gratuit!" />
        <meta name="keywords" content="taxi bruxelles, taxi bruxelles 24/7, réserver taxi bruxelles, taxi bruxelles prix, taxi bruxelles pas cher, taxi gare bruxelles, taxi centre bruxelles" />
        <link rel="canonical" href="https://www.luxurytransferservices.be/taxi-bruxelles" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Taxi Bruxelles 24/7 | Service Premium & Prix Fixes" />
        <meta property="og:description" content="Service de taxi premium à Bruxelles. Réservation en ligne, prix fixes, chauffeurs professionnels. Disponible 24h/24." />
        <meta property="og:url" content="https://www.luxurytransferservices.be/taxi-bruxelles" />
        
        {/* Schema.org markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TaxiService",
            "name": "Luxury Transfer Service - Taxi Bruxelles",
            "description": "Service de taxi premium à Bruxelles disponible 24h/24",
            "url": "https://www.luxurytransferservices.be/taxi-bruxelles",
            "telephone": "+32-2-315-02-54",
            "priceRange": "€€€",
            "areaServed": {
              "@type": "City",
              "name": "Bruxelles"
            },
            "availableChannel": {
              "@type": "ServiceChannel",
              "serviceUrl": "https://www.luxurytransferservices.be",
              "availableLanguage": ["fr", "en", "nl"]
            },
            "openingHours": "Mo-Su 00:00-23:59",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "150"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
        <Navbar />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Taxi Bruxelles <span className="text-gold">24/7</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8">
                Service de taxi premium disponible jour et nuit dans toute la région bruxelloise
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-gold hover:bg-amber-600 text-black text-lg px-8">
                  <Phone className="w-5 h-5 mr-2" />
                  Réserver Maintenant
                </Button>
                <Button size="lg" variant="outline" className="border-gold text-gold hover:bg-gold/10 text-lg px-8">
                  Calculer Mon Prix
                </Button>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
              <div className="text-center">
                <div className="text-3xl font-bold text-gold">24/7</div>
                <div className="text-sm text-gray-400">Service Non-Stop</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gold">150+</div>
                <div className="text-sm text-gray-400">Avis Clients</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gold">4.8★</div>
                <div className="text-sm text-gray-400">Note Moyenne</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gold">5 Min</div>
                <div className="text-sm text-gray-400">Temps Réponse</div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            {/* Introduction */}
            <div className="prose prose-invert max-w-none mb-16">
              <h2 className="text-3xl font-bold text-white mb-6">
                Votre Taxi à Bruxelles : Service Premium et Fiable
              </h2>
              <p className="text-lg text-gray-300 leading-relaxed mb-4">
                Vous cherchez un <strong>taxi à Bruxelles</strong> fiable, confortable et disponible à toute heure ? 
                Luxury Transfer Service est votre solution de transport premium dans la capitale belge. 
                Nous offrons un service de <strong>taxi professionnel à Bruxelles</strong> avec des chauffeurs expérimentés 
                et des véhicules de luxe pour tous vos déplacements.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed mb-4">
                Que ce soit pour un <strong>transfert vers l'aéroport de Bruxelles</strong>, une course en ville, 
                un déplacement professionnel dans le <strong>quartier européen</strong>, ou une sortie nocturne, 
                notre service de <strong>taxi Bruxelles 24h/24</strong> est à votre disposition. 
                Réservez en ligne en quelques clics et profitez de <strong>prix fixes</strong> sans surprise.
              </p>
            </div>

            {/* Services Grid */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-white mb-8 text-center">
                Nos Services de Taxi à Bruxelles
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {services.map((service) => (
                  <Card key={service.title} className="bg-gray-800/50 border-gold/20 hover:border-gold/50 transition-all">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-semibold text-gold mb-3">{service.title}</h3>
                      <p className="text-gray-300 mb-4">{service.description}</p>
                      <Link to={service.link}>
                        <Button variant="outline" size="sm" className="border-gold/30 text-gold hover:bg-gold/10">
                          En savoir plus →
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-white mb-8 text-center">
                Pourquoi Choisir Notre Taxi à Bruxelles ?
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                    <span className="text-gray-300">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing Section */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-white mb-8 text-center">
                Prix Taxi Bruxelles : Tarifs Transparents
              </h2>
              <div className="bg-gradient-to-r from-gold/10 to-amber-900/10 border border-gold/30 rounded-lg p-8">
                <p className="text-lg text-gray-300 mb-6">
                  Chez Luxury Transfer Service, nous pratiquons des <strong>tarifs fixes et transparents</strong> pour tous nos services de taxi à Bruxelles. 
                  Pas de compteur qui défile, pas de mauvaise surprise : vous connaissez le prix avant même de monter à bord.
                </p>
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  <div>
                    <Euro className="w-12 h-12 text-gold mx-auto mb-3" />
                    <h3 className="text-xl font-semibold text-white mb-2">Prix de Base</h3>
                    <p className="text-gray-400">À partir de 8€ + distance</p>
                  </div>
                  <div>
                    <Shield className="w-12 h-12 text-gold mx-auto mb-3" />
                    <h3 className="text-xl font-semibold text-white mb-2">Sans Surprise</h3>
                    <p className="text-gray-400">Prix fixe confirmé avant réservation</p>
                  </div>
                  <div>
                    <Star className="w-12 h-12 text-gold mx-auto mb-3" />
                    <h3 className="text-xl font-semibold text-white mb-2">Qualité Premium</h3>
                    <p className="text-gray-400">Véhicules luxe au même prix</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Coverage Area */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-white mb-8 text-center">
                Zones Desservies par Notre Taxi Bruxelles
              </h2>
              <p className="text-lg text-gray-300 mb-6 text-center">
                Notre service de taxi couvre l'ensemble de la région de Bruxelles-Capitale et au-delà :
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="bg-gray-800/50 border-gold/20">
                  <CardContent className="p-6">
                    <MapPin className="w-8 h-8 text-gold mb-3" />
                    <h3 className="text-lg font-semibold text-white mb-3">Centre-Ville</h3>
                    <ul className="text-sm text-gray-400 space-y-1">
                      <li>• Gare Centrale</li>
                      <li>• Grand Place</li>
                      <li>• Quartier Européen</li>
                      <li>• Quartier Royal</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card className="bg-gray-800/50 border-gold/20">
                  <CardContent className="p-6">
                    <MapPin className="w-8 h-8 text-gold mb-3" />
                    <h3 className="text-lg font-semibold text-white mb-3">Communes</h3>
                    <ul className="text-sm text-gray-400 space-y-1">
                      <li>• Ixelles, Uccle, Woluwe</li>
                      <li>• Schaerbeek, Etterbeek</li>
                      <li>• Saint-Gilles, Forest</li>
                      <li>• Toutes les 19 communes</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card className="bg-gray-800/50 border-gold/20">
                  <CardContent className="p-6">
                    <MapPin className="w-8 h-8 text-gold mb-3" />
                    <h3 className="text-lg font-semibold text-white mb-3">Périphérie</h3>
                    <ul className="text-sm text-gray-400 space-y-1">
                      <li>• Aéroport Zaventem</li>
                      <li>• Waterloo, Wavre</li>
                      <li>• Tervuren, Kraainem</li>
                      <li>• Zone élargie Brabant</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-white mb-8 text-center">
                Questions Fréquentes - Taxi Bruxelles
              </h2>
              <div className="space-y-4">
                <details className="bg-gray-800/50 border border-gold/20 rounded-lg p-6">
                  <summary className="text-lg font-semibold text-white cursor-pointer">
                    Comment réserver un taxi à Bruxelles ?
                  </summary>
                  <p className="text-gray-300 mt-4">
                    Réservez facilement en ligne sur notre site, par téléphone au +32 2 315 02 54, ou via notre formulaire de contact. 
                    La réservation prend moins de 2 minutes et vous recevez une confirmation immédiate.
                  </p>
                </details>
                <details className="bg-gray-800/50 border border-gold/20 rounded-lg p-6">
                  <summary className="text-lg font-semibold text-white cursor-pointer">
                    Quel est le prix d'un taxi à Bruxelles ?
                  </summary>
                  <p className="text-gray-300 mt-4">
                    Nos tarifs commencent à 8€ (prix de base) + coût au kilomètre selon la distance. 
                    Utilisez notre calculateur en ligne pour obtenir un devis précis et instantané avant de réserver.
                  </p>
                </details>
                <details className="bg-gray-800/50 border border-gold/20 rounded-lg p-6">
                  <summary className="text-lg font-semibold text-white cursor-pointer">
                    Êtes-vous disponibles 24h/24 ?
                  </summary>
                  <p className="text-gray-300 mt-4">
                    Oui ! Notre service de taxi à Bruxelles est disponible 24 heures sur 24, 7 jours sur 7, 
                    même les jours fériés. Que ce soit pour un vol tôt le matin ou une sortie tardive, nous sommes là.
                  </p>
                </details>
                <details className="bg-gray-800/50 border border-gold/20 rounded-lg p-6">
                  <summary className="text-lg font-semibold text-white cursor-pointer">
                    Proposez-vous des transferts vers l'aéroport ?
                  </summary>
                  <p className="text-gray-300 mt-4">
                    Absolument ! Nous proposons des <Link to="/taxi-aeroport-bruxelles" className="text-gold hover:underline">transferts vers les aéroports de Zaventem et Charleroi</Link>. 
                    Service de prise en charge et dépose avec suivi de vol pour les arrivées.
                  </p>
                </details>
              </div>
            </div>

            {/* CTA Final */}
            <div className="bg-gradient-to-r from-gold to-amber-600 rounded-lg p-8 text-center">
              <h2 className="text-3xl font-bold text-black mb-4">
                Besoin d'un Taxi à Bruxelles Maintenant ?
              </h2>
              <p className="text-lg text-black mb-6">
                Réservez en ligne en 2 minutes ou appelez-nous pour une prise en charge immédiate
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-black hover:bg-gray-900 text-white">
                  <Phone className="w-5 h-5 mr-2" />
                  +32 2 315 02 54
                </Button>
                <Button size="lg" variant="outline" className="border-black text-black hover:bg-black/10">
                  Réserver En Ligne
                </Button>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default TaxiBruxelles;
